import Clarity from "@microsoft/clarity";

type ClarityIdentity = {
  customId?: string;
  friendlyName?: string;
  traits?: Record<string, string | undefined>;
};

const projectId = process.env.EXPO_PUBLIC_CLARITY_PROJECT_ID?.trim();
const SESSION_STORAGE_KEY = "clarity_custom_session_id";

let started = false;
let customId: string | undefined;
let friendlyName: string | undefined;
let pageId: string | undefined;
let customSessionId: string | undefined;
let warnedMissingProjectId = false;

const createId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

// One id per browser tab session, so recordings can be filtered by custom session id.
const getCustomSessionId = () => {
  if (customSessionId) return customSessionId;

  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY)?.trim();
    customSessionId = existing || createId();
    if (!existing) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, customSessionId);
    }
  } catch {
    customSessionId = createId();
  }

  return customSessionId;
};

const canTrack = () =>
  Boolean(projectId) &&
  typeof window !== "undefined" &&
  typeof document !== "undefined";

const ensureStarted = () => {
  if (!canTrack()) {
    if (!projectId && !warnedMissingProjectId && __DEV__) {
      warnedMissingProjectId = true;
      console.warn(
        "Microsoft Clarity is not configured. Set EXPO_PUBLIC_CLARITY_PROJECT_ID."
      );
    }
    return false;
  }

  if (started) return true;

  Clarity.init(projectId as string);
  started = true;

  const environment = process.env.EXPO_PUBLIC_DATADOG_ENVIRONMENT?.trim();
  if (environment) {
    Clarity.setTag("environment", environment);
  }

  return true;
};

// Identify API: custom id is required; session, page, and friendly name are optional.
// Clarity asks for this on every page so the current route stays attached to the user.
const identifyCurrentPage = () => {
  if (!ensureStarted() || !customId) return;

  try {
    Clarity.identify(customId, getCustomSessionId(), pageId, friendlyName);
    if (pageId) {
      Clarity.setTag("screen", pageId);
    }
  } catch (error) {
    if (__DEV__) {
      console.warn("Microsoft Clarity identify failed", error);
    }
  }
};

const setUserTraits = (traits?: Record<string, string | undefined>) => {
  if (!ensureStarted() || !traits) return;

  Object.entries(traits).forEach(([key, value]) => {
    if (value?.trim()) {
      Clarity.setTag(key, value);
    }
  });
};

export const initMicrosoftClarity = () => {
  ensureStarted();
};

export const identifyClarityUser = ({
  customId: nextCustomId,
  friendlyName: nextFriendlyName,
  traits,
}: ClarityIdentity) => {
  const nextId = nextCustomId?.trim();
  if (!nextId || nextId === "undefined" || nextId === "null") return;

  customId = nextId;
  friendlyName = nextFriendlyName?.trim() || undefined;
  setUserTraits(traits);
  identifyCurrentPage();
};

export const trackClarityScreen = (nextPageId?: string) => {
  const next = nextPageId?.trim();
  if (!next || next === "Unknown" || next === pageId) return;

  pageId = next;
  if (!ensureStarted()) return;

  try {
    Clarity.setTag("screen", pageId);
    Clarity.event("screen_view");
    identifyCurrentPage();
  } catch (error) {
    if (__DEV__) {
      console.warn("Microsoft Clarity screen tracking failed", error);
    }
  }
};
